/**
 * Cloud runs (D184): the tasks team members sent to a provider's cloud agent, as the organisation
 * recorded them. Who started each, which provider, member and task, how long it ran, the pull
 * request it came back as and how it ended; never the task's text or what its tools said. A
 * project shows its own; elsewhere administrators see every run and anyone else their own. A list group
 * (Layout.tsx): one row a run, its ending as a mark.
 */

import type { CloudRun } from '@aioe/shared';
import { Cloud } from '@gravity-ui/icons';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { CLOUD_PROVIDER_TEXT } from './policyEditor.tsx';
import { duration } from './projectRuns.tsx';
import { SubjectName } from './SubjectPicker.tsx';
import { Empty, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import type { ArtTone } from './ui/Art.tsx';

/** How a run ended, from what the workbench reported. */
function ending(r: CloudRun): { state: 'working' | 'connected' | 'idle' | 'error'; label: string } {
  if (r.state === 'open') return { state: 'working', label: 'In the cloud' };
  if (r.state === 'expired') return { state: 'idle', label: 'Expired' };
  const kinds = new Set(r.events.map((e) => e.kind));
  if (kinds.has('failed')) return { state: 'error', label: 'Failed' };
  if (kinds.has('cancelled')) return { state: 'idle', label: 'Cancelled' };
  if (kinds.has('integrated')) return { state: 'connected', label: 'Integrated' };
  if (kinds.has('handed-back')) return { state: 'connected', label: 'Handed back' };
  return { state: 'idle', label: 'Ended' };
}

/** How long a run went: until it ended, until its credential lapsed, or so far. */
const runMs = (r: CloudRun): number => Math.max(0, (r.endedAt ? Date.parse(r.endedAt) : r.state === 'expired' ? Date.parse(r.expiresAt) : Date.now()) - Date.parse(r.createdAt));

/** `hideWhenEmpty`: nothing at all until a run exists (Activity, where an empty panel would only add noise). */
export function CloudRuns({ tone, projectId, showProject, hideWhenEmpty }: { tone: ArtTone; projectId?: string; showProject?: boolean; hideWhenEmpty?: boolean }) {
  const data = useLoad(() => routes.cloudRuns({ project: projectId, limit: 200 }), [projectId], 30_000);
  const runs = data.data?.runs ?? [];
  if (hideWhenEmpty && !runs.length && !data.error) return null;
  const open = runs.filter((r) => r.state === 'open').length;
  return (
    <ListGroup
      title="Cloud runs"
      count={runs.length || undefined}
      description={open ? `${open} working in the cloud now. Tasks team members sent to a provider’s cloud agent.` : 'Tasks team members sent to a provider’s cloud agent.'}
      empty={data.data && runs.length === 0 ? <Empty tone={tone} icon={<Cloud />} compact title="No cloud runs yet" body="When a team member placed in the cloud takes a task, its workbench registers the run here: who started it, the provider, the pull request it came back as and how it ended." /> : undefined}
    >
      {data.error ? (
        <ListBlock>
          <ErrorNote error={data.error} />
        </ListBlock>
      ) : null}
      {!data.data && !data.error ? <ListBlock className="text-sm text-muted">Reading the cloud runs…</ListBlock> : null}
      {runs.map((r) => {
        const end = ending(r);
        return (
          <ListRow
            key={r.id}
            title={r.member}
            description={
              <>
                <span className="block">
                  {CLOUD_PROVIDER_TEXT[r.provider]} · started by <SubjectName id={r.startedBy} /> · {timeAgo(r.createdAt)} · ran <span className="tabular-nums">{duration(runMs(r))}</span>
                </span>
                <span className="block">
                  {showProject && r.projectId ? `${r.projectId} · ` : ''}task {r.task} · team {r.team} · <span className="tabular-nums">{r.mcp ? `${r.callCount} tool ${r.callCount === 1 ? 'call' : 'calls'}` : 'no live tools'}</span>
                  {r.endReason ? ` · ${r.endReason}` : ''}
                  {r.pullRequest ? (
                    <>
                      {' · '}
                      <a href={r.pullRequest} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                        Pull request
                      </a>
                    </>
                  ) : null}
                  {r.url ? (
                    <>
                      {' · '}
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                        On {CLOUD_PROVIDER_TEXT[r.provider]}
                      </a>
                    </>
                  ) : null}
                </span>
              </>
            }
            status={<StatusMark state={end.state} label={end.label} />}
          />
        );
      })}
    </ListGroup>
  );
}
